import { h, Component } from 'preact';
import cx from 'classnames';
import { Subscription, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DivCatUtils } from '../../utils/div-cat-utils';
import { Form, Field, FieldList } from '../../utils/forms/form';
import * as Validators from '../../utils/forms/validators';
import { 
  residentialPropTypes, LeaseTerms, FurnishingChoices, 
  commercialPropTypes, REQUIRES_BUILDING, 
  ConditionChoices, MinimumRentalPrice
} from '../../../constants/properties';

import hopplerLinkState, { hopplerUpdateFormFieldValue } from '../../utils/forms/hoppler-linkstate';
import { options } from '../../../constants/options';

import {
  DivCat, Location, RequestState, REQUEST_IN_PROGRESS, REQUEST_SUCCESS,
  REQUEST_FAILED, REQUEST_UNTOUCHED, Listing, KumitaMultimedia
} from '../../../types';
import { PostPropertyPhoto } from './listings/post-property-photo.component';
import { PostPropertyLocation } from './listings/post-property-location.component';

import getStore, { appState$ } from '../store';
import {
  ListingAction, POST_PROPERTY, SAVE_AS_DRAFT, GENERATE_DESCRIPTION_PREFILL,
  RESET_POST_PROPERTY
} from '../reducers/listing/listing.actions';

import { CurrencyInput } from './common/hoppler-currency-input.component';
import { DiscountInput } from './common/hoppler-discount-input.component';
import { cxValidateField } from '../../utils/forms/validators';
import { constructFields } from '../form-fields/post-property.fields';
import { createKumitaListing } from '../../utils/create-api-input';
import { PropertyPage } from './propertypreview/property-page';
import { ToggleSwitch } from './common/toggle-switch.component';
import { NumberInput } from './common/hoppler-number-input.component';
import { UploadablePhoto } from '../../../types/uploadable-photo.model';
import { photoUploadService } from '../../utils/photo-upload.service';
import { DatePickerControl } from './common/date-picker-control.component';
import { distinctUntilChangedImmutable } from 'distinct-until-changed-immutable';

/** 
 * 
 * Hello and welcome to the post property component.
 * First thing's first, it's huge. More than I hoped for.
 * But it's definitely smaller than frontendv2's Post property (fo sho)
 * 
 * Anyway, this component can still be reduced and its functions can be
 * refactored out to utilities and the such to reduce this file
 * and make it easier to read for future purposes, but as it stands,
 * pushing out a stable and working product was the priorty
 * and so this was born.
 * 
 * */ 

export interface PostPropertyProps {
}

export interface PostPropertyState {
  currentStage?: string;

  // This is to hold returned data from the API
  // that is used to enter the property description prefill
  currentListing?: Listing; 

  currentDivCat?: DivCat;
  currentLocation?: Location;
  locationForm?: Form;
  multimedia?: KumitaMultimedia[];

  addreNoLabel?: string;
  searchFieldLabel?: string;
  requiresBuilding?: boolean;
  map?: any;

  photosForUpload?: UploadablePhoto[];
  isReviewingPhotos?: boolean;
  hasUploadedPhotos?: boolean;
  photoUploadState?: RequestState;

  postPropertyForm?: Form;

  hasDupe?: boolean;
  isPresale?: boolean;
  discountedPercentage?: number;

  descriptionPrefill?: string;

  submitPropertyStatus?: RequestState;
  submitDraftStatus?: RequestState;
  retrieveDescPrefillStatus?: RequestState;
  
  isPpsqmNotEnough?: boolean;
}

// -- Mirroring frontendv2 data values --

export const DIVISION_RESIDENTIAL = 1;
export const DIVISION_COMMERCIAL = 2;

export const CATEGORY_RENT = 1;
export const CATEGORY_SALE = 2;

// -- Mirroring frontendv2 data values --

export class PostProperty extends Component<PostPropertyProps, PostPropertyState> {
  form$: Subject<Form>;
  dcUtils = new DivCatUtils();
  private subscriptions: Subscription[] = [];
  
  constructor(props: PostPropertyProps) {
    super(props);

    // fun fact, you can set the stage here to location or photos if you need
    // to skip and a specific thing
    this.setState({
      currentStage: 'type',
      currentDivCat: null,
      currentLocation: null,
      photosForUpload: [],
      isPresale: false
    });

    this.setupForm(props);
  }

  setupForm(props: PostPropertyProps) {
    let fields: FieldList = constructFields(props);

    this.form$ = new Subject<Form>();
    this.setState({
      postPropertyForm: new Form(fields)
    });

    this.subscriptions.push(
      Form.bind(this, this.form$, {
        key: 'postPropertyForm',
        onChange: postPropertyForm => {
          this.setState({ postPropertyForm });
          // console.log(postPropertyForm)
        }
      })
    );

    this.subscriptions.push(
      this.form$.pipe(
        map(form => ({
          division: form.fields.division.value,
          category: form.fields.category.value,
          type: form.fields.type.value,
        }) as DivCat),
        distinctUntilChangedImmutable()
      ).subscribe(currentDivCat => this.setState({ currentDivCat }))
    );

    this.subscriptions.push(
      this.form$.pipe(
        map(form => form.fields['category'].value),
        distinctUntilChanged()
      ).subscribe((category) => {
        let currentDivCat = { ...this.state.currentDivCat };

        if (category && currentDivCat) {
          currentDivCat.category = category;

          if (category === 'Rent') {
            hopplerUpdateFormFieldValue(
              this.form$,
              this.state.postPropertyForm,
              'rentalPrice',
              0
            );
          } else if (category === 'Sale') {
            hopplerUpdateFormFieldValue(
              this.form$,
              this.state.postPropertyForm,
              'salePrice',
              0
            );
          }

          // console.log('Category watch -- DivCat:', currentDivCat);
          this.setState({ currentDivCat });
        }
      })
    );

    this.subscriptions.push(
      this.form$.pipe(
        map(form => form.fields['type'].value),
        distinctUntilChanged()
      ).subscribe((type) => {
        // console.log('Type changed!', type);
        if (type) {
          let form = this.state.postPropertyForm;
          let locationLabel = 'Area name';
          let addreNoLabel = 'Lot number';

          const requiresBuilding = REQUIRES_BUILDING.find(member => {
            return member.label === form.fields['type'].value;
          });

          if (requiresBuilding) {
            locationLabel = 'Building name';
            addreNoLabel = 'Unit number';
          } else if (form.fields['type'].value === 'House and Lot' ||
            form.fields['type'].value === 'Townhouse') {
            addreNoLabel = 'House number';
          } else if (form.fields['type'].value === 'Warehouse') {
            addreNoLabel = 'Unit number';
          } else if (
            form.fields['type'].value === 'Building') {
            locationLabel = 'Area name';
            addreNoLabel = 'Street number';
          } else if (form.fields['type'].value === 'Resort') {
            locationLabel = 'Area name';
            addreNoLabel = 'Unit number';
          }

          this.setState({
            addreNoLabel: addreNoLabel,
            searchFieldLabel: locationLabel,
            requiresBuilding: requiresBuilding ? true : false
          });
        }
      })
    );

    this.subscriptions.push(
      this.form$.pipe(
        map(form => form.fields['pricePerSqm'].value)
      ).subscribe(pricePerSqm => {
        let area;

        if (this.state.postPropertyForm.fields['floorArea'].value) {
          area = this.state.postPropertyForm.fields['floorArea'].value;
        }

        if (this.state.postPropertyForm.fields['lotArea'].value) {
          area = this.state.postPropertyForm.fields['lotArea'].value;
        }

        const calculatedRentalPrice = area * pricePerSqm;

        if (calculatedRentalPrice < MinimumRentalPrice) {
          this.setState({
            isPpsqmNotEnough: true
          });
        } else {
          this.setState({
            isPpsqmNotEnough: false
          });
        }
      })
    );
  }

  resetComponent = () => {
    let copy = Object.assign(
      {},
      this.state
    );

    for (let k in copy) {
      copy[k] = undefined;
    }

    this.setState(copy);

    this.setState({
      currentStage: 'type',
      currentDivCat: null,
      currentLocation: null,
      photosForUpload: []
    });

    this.setupForm(this.props);

    const store = getStore();
    store.dispatch({
      type: RESET_POST_PROPERTY
    })
  }

  componentDidMount() {
    const listing$ = appState$.pipe(
      map(app => app.listing)
    );

    this.subscriptions.push(
      listing$.pipe(
        map(listing => listing.submitPropertyStatus),
        distinctUntilChanged()
      ).subscribe(submitPropertyStatus => {

        this.setState({
          submitPropertyStatus: submitPropertyStatus
        });

        if (submitPropertyStatus === REQUEST_SUCCESS) {
          this.setState({
            currentStage: 'submit'
          });
        }

      })
    );

    this.subscriptions.push(
      listing$.pipe(
        map(listing => listing.submitDraftStatus),
        distinctUntilChanged()
      ).subscribe(submitDraftStatus => {
        this.setState({
          submitDraftStatus: submitDraftStatus
        });
      })
    );

    this.subscriptions.push(
      listing$.pipe(
        map(listing => listing.retrieveDescPrefillStatus),
        distinctUntilChanged()
      ).subscribe(retrieveDescPrefillStatus => {
        this.setState({
          retrieveDescPrefillStatus: retrieveDescPrefillStatus
        });
      })
    );

    this.subscriptions.push(
      listing$.pipe(
        map(listing => listing.descriptionPrefill)
      ).subscribe(descriptionPrefill => {
        if (descriptionPrefill) {
          let listing = this.state.currentListing;
          listing.descriptionPrefill = descriptionPrefill;

          this.setState({
            descriptionPrefill: descriptionPrefill,
            currentListing: listing
          });

          if (this.state.currentStage === 'essentials') {
            this.onTabHeaderClicked('review');
          }
        }
      })
    );

  }

  componentWillUnmount() {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
  }

  postProperty = (addedDesc?: string) => {
    let apiProperty = createKumitaListing(
      this.state.postPropertyForm,
      this.state.currentListing,
      this.state.currentLocation,
      this.state.locationForm,
      this.state.multimedia
    );

    // addedDesc can be undefined if no description is entered.
    apiProperty.description = apiProperty.descriptionPrefill.concat(
      ' ' + (addedDesc || '')
    );
    const store = getStore();
    const action: ListingAction = {
      type: POST_PROPERTY,
      payload: {
        apiProperty: apiProperty
      }
    }
    store.dispatch(action);
  }

  saveAsDraft = () => {
    let incompleteListing = createKumitaListing(
      this.state.postPropertyForm,
      null,
      this.state.currentLocation,
      this.state.locationForm,
      this.state.multimedia
    );

    incompleteListing['saveAs'] = 'Draft'; 

    const store = getStore();
    const action: ListingAction = {
      type: SAVE_AS_DRAFT,
      payload: {
        apiProperty: incompleteListing
      }
    }

    store.dispatch(action);
  }

  generateListingForDescription = () => {

    let incompleteListing = createKumitaListing(
      this.state.postPropertyForm,
      null,
      this.state.currentLocation,
      this.state.locationForm,
      this.state.multimedia
    );

    const store = getStore();
    const action: ListingAction = {
      type: GENERATE_DESCRIPTION_PREFILL,
      payload: {
        apiProperty: incompleteListing
      }
    }
    store.dispatch(action);

    this.setState({
      currentListing: incompleteListing
    });
  }

  ondateAvailabilitySelected = (date: string) => {
    this.handleDatepickerValidation(date, 'dateAvailability');
}

  handleDatepickerValidation = (date: string, fieldName: string) => {
    const currentForm = this.state.postPropertyForm; 

    // Manually do this because in order to follow the 
    // hopplerLinkstate validation system
    let clonedFields: FieldList = {};
    for (let k in currentForm.fields) {
      let prevField = currentForm.fields[k];
      clonedFields[k] = new Field(prevField.validators, prevField.customValidators, prevField.value);
      clonedFields[k].isValid = prevField.isValid;
    }

    let clone: Form = new Form(clonedFields);
    clone.fields[fieldName].value = date;
    clone.fields[fieldName].isPristine = false;
    // clone.fields[fieldName].customValidators.forEach(a => a(clone.fields[fieldName], clone));
    this.form$.next(clone);
  }

  getTabContentBlock(props: PostPropertyProps, state: PostPropertyState) {
    switch (state.currentStage) {
      case 'type':
        return this.getTypeTabBlock(props, state);

      case 'location':
        return this.getLocationTabBlock(props, state);

      case 'photos':
        return this.getPhotosTabBlock(props, state);

      case 'essentials':
        return this.getEssentialsTabBlock(props, state);
    }

  }

  // -------- TYPE SECTION --------

  getTypeTabBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;

    return (
      <div className="hi-postprop__tab-content__type">
        <div className="row">
          <div className="col-12 col-lg-6">
            <label className="hi-listing-editor-modal__editor__label">
              Property Kind*
              </label>

            <div className="row mx-0">
              <span className={ cx('hi-input-radio-toggle col-6', 
                  {
                    active: form.fields['division'].value == 'Residential'
                  })
                }>
                <label>
                  <input className="first" type="radio" name="propertyDivision" 
                    value="Residential"
                    onChange={ hopplerLinkState(this.form$, form, 'division') }/>
                  Residential
                </label>
              </span>
              <span className={ cx('hi-input-radio-toggle col-6', 
                  {
                    active: form.fields['division'].value == 'Commercial'
                  })
                }>
                <label>
                  <input className="first" type="radio" name="propertyDivision"
                    value="Commercial"
                    onChange={ hopplerLinkState(this.form$, form, 'division') }/>
                  Commercial
                </label>
              </span>
            </div>
            
          </div>
          <div className="col-12 col-lg-6 mt-3 mt-lg-0">
            <label className="hi-listing-editor-modal__editor__label">
              Category*
            </label>

            <div className="row mx-0">
              <span className={ cx('hi-input-radio-toggle col-6', 
                  {
                    active: form.fields['category'].value == 'Rent'
                  })
                }>
                <label>
                  <input  className="first" type="radio" name="category" value="Rent"
                    checked={ form.fields.category.value === 'Rent' }
                    onChange={ hopplerLinkState(this.form$, form, 'category') } />
                  For rent
                </label>
              </span>
              <span className={ cx('hi-input-radio-toggle col-6', 
                  {
                    active: form.fields['category'].value == 'Sale'
                  })
                }>
                <label>
                  <input className="first" type="radio" name="category" value="Sale"
                    checked={ form.fields.category.value === 'Sale' }
                    onChange={ hopplerLinkState(this.form$, form, 'category') } />
                  For sale
                </label>
              </span>
            </div>                
          </div>
        </div>

        <div className="row">
          <div className="col-12 mt-4">
            <label className="hi-listing-editor-modal__editor__label">
              Property Type*
            </label>

            { this.getPropertyTypes(state) }
          </div>
        </div>
        
      </div>
    );
  }

  getPropertyTypes(state: PostPropertyState) {
    let form = state.postPropertyForm;

    if (form.fields['division'].value == 'Residential') {
      return (
        <div className="row">
          {
            residentialPropTypes.map((option) => 
            <span className="hi-input-group__checkbox col-sm-12 col-md-3 mr-0 d-flex">
              <label>
                <input
                  type="radio" name="type" 
                  onChange={ hopplerLinkState(this.form$, form, 'type') }
                  value={option.label} 
                  checked={form.fields['type'].value == option.label}
                  />
                { option.label }
              </label>
            </span>
            )
          }
        </div>
      );
    } else {
      let commercialTypesToUse;

      if (form.fields['category'].value == 'Rent') {
        commercialTypesToUse = commercialPropTypes.filter(type => {
          if (type.label != 'Industrial Lot' && type.label != 'Resort') {
            return type;
          }
        });
      } else {
        commercialTypesToUse = commercialPropTypes.filter(type => {
          if (type.label != 'Serviced Office') {
            return type;
          }
        });
      }

      return (
        <div className="row">
          {
            commercialTypesToUse.map((option) => 
            <span className="hi-input-group__checkbox col-sm-12 col-md-3 mr-0 d-flex align-items-center justify-content-start">
              <label>
                <input
                  type="radio" name="type" 
                  onChange={ hopplerLinkState(this.form$, form, 'type') }
                  value={option.label} 
                  checked={form.fields['type'].value == option.label}
                  />
                { option.label }
              </label>
            </span>
            )
          }
        </div>
      );
    }
  }

  // -------- LOCATION SECTION --------
  onLocationChange = (location: Location) => {
    // console.log('Post Property: Location set', location);
    this.setState({
      currentLocation: location
    });
  }

  onUpdateFormFields = (form: Form) => {
    this.setState({
      locationForm: form
    });
  }

  onDupeCheck = (hasDupe: boolean) => {
    this.setState({
      hasDupe: hasDupe
    });
  }

  getLocationTabBlock(props: PostPropertyProps, state: PostPropertyState) {
    return (
      <PostPropertyLocation
        canEdit={true}
        isPostProperty={true}
        addreNoLabel={state.addreNoLabel}
        searchFieldLabel={state.searchFieldLabel}
        requiresBuilding={state.requiresBuilding}
        currentDivCat={state.currentDivCat}
        onLocationChange={this.onLocationChange}
        currentLocation={state.currentLocation}
        onUpdateFormFields={this.onUpdateFormFields}
        onDupeCheck={this.onDupeCheck}/>
    );
  }

  // -------- PHOTOS SECTION --------

  onUploadPhotos = async () => {
    const photos = this.state.photosForUpload;

    this.setState({
      photoUploadState: REQUEST_IN_PROGRESS
    });

    let uploadedPhotos: KumitaMultimedia[];

    try {
      uploadedPhotos = await photoUploadService.upload(photos);
      if (!this.state.multimedia || this.state.multimedia.length < 1) {
        uploadedPhotos[0].isPrimary = 1;
      } else {
        uploadedPhotos = [ ...this.state.multimedia, ...uploadedPhotos ];
      }

      this.setState({
        hasUploadedPhotos: true,
        multimedia: uploadedPhotos,
        photoUploadState: REQUEST_SUCCESS,
        isReviewingPhotos: false,
        photosForUpload: []
      });
    } catch (err) {
      console.error(err);

      this.setState({
        photoUploadState: REQUEST_FAILED,
        isReviewingPhotos: false
      });
    }
  }

  onCancelPhotoUpload = () => {
    this.setState({
      photosForUpload: [],
      isReviewingPhotos: false
    });
  }

  onPhotoReviewStateChange = (inReview: boolean) => {
    this.setState({
      isReviewingPhotos: inReview,
      photoUploadState: REQUEST_UNTOUCHED
    });
  }

  getPhotosTabBlock(props: PostPropertyProps, state: PostPropertyState) {
    return (
      <PostPropertyPhoto 
        onReviewStateChange={ inReview => this.onPhotoReviewStateChange(inReview) }

        hasUploaded={state.hasUploadedPhotos}
        photoUploadState={state.photoUploadState}

        uploadedPhotos={state.multimedia}
        onUploadedPhotosChange={ multimedia => this.setState({ multimedia }) }

        photosForUpload={ state.photosForUpload }
        onPhotoSelectionChange={ photosForUpload => this.setState({ photosForUpload }) } />
    );
  }

  // -------- ESSENTIALS SECTION --------

  getEssentialsTabBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    let showDiscount = options.discountEnabled;
    if (this.state.isPresale) { showDiscount = false};
    if (state.currentDivCat.division === 'Commercial') { showDiscount = false};
    
    return (
      <div className="hi-postprop__tab-content__essentials">

        { state.currentDivCat.category === 'Sale' ?
        <div className="row mt-1">
          <div className="col-12">
            <ToggleSwitch onChange={ this.onIsPresellingChange }
              value={ state.isPresale }
              label='Pre-selling'
            />
          </div>
        </div>
        : null }

        { this.getEssentialsPriceBlock(props, state) }

        { showDiscount ?
          this.getEssentialsDiscountBlock(props, state)
        : null }

        { this.getMinLeaseTermBlock(props, state) }

        <div className="row mt-4">
          <div className="col-6">
            <label className="hi-input-label">
              Can the property owner issue an official receipt? *
            </label>

            <div className="row mx-0 mb-1">
              <span className={ cx('hi-input-radio-toggle col-6',
                  {
                    active: form.fields['ownerReceipt'].value == 1
                  }) 
                  }>
                <label>
                  <input className="first" 
                    type="radio"
                    name="ownerReceipt" 
                    value={1}
                    onChange={ hopplerLinkState(this.form$, form, 'ownerReceipt') }/>
                  Yes
                </label>
              </span>
              <span className={ cx('hi-input-radio-toggle col-6',
                  {
                    active: form.fields['ownerReceipt'].value == 0
                  }) 
                  }>
                <label>
                  <input className="first" 
                    type="radio"
                    name="ownerReceipt" 
                    value={0}
                    onChange={ hopplerLinkState(this.form$, form, 'ownerReceipt') }/>
                  No
                </label>
              </span>
            </div>
            { this.getErrorBlock(state, 'ownerReceipt', 'Please choose if the owner provides receipts') }
          </div>
          
          {
            state.currentDivCat.category === 'Rent' ?
              <div className="col-6">
                <label className="hi-input-label">
                  Are the association dues included in the rental price? *
                </label>

                <div className="row mx-0 mb-1">
                  <span className={ cx('hi-input-radio-toggle col-6',
                        {
                          active: form.fields['associationDuesIncluded'].value == 1
                        }) 
                      }>
                    <label>
                      <input className="first" 
                        type="radio"
                        name="associationDuesIncluded"
                        value={ 1 }
                        onChange={ hopplerLinkState(this.form$, form, 'associationDuesIncluded') }/>
                      Yes
                    </label>
                  </span>
                  <span className={ cx('hi-input-radio-toggle col-6',
                        {
                          active: form.fields['associationDuesIncluded'].value == 0
                        }) 
                      }>
                    <label>
                      <input 
                        className="first" 
                        type="radio"
                        name="associationDuesIncluded"
                        value={ 0 }
                        onChange={ hopplerLinkState(this.form$, form, 'associationDuesIncluded') }/>
                      No
                    </label>
                  </span>
                </div>
                { this.getErrorBlock(state, 'associationDuesIncluded', 'Please choose if Association dues are included') }
              </div> : null
          }

          {
            form.fields['associationDuesIncluded'].value == 0 ?
            <div className="col-12 mt-3">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Association Dues*</label>
                  <CurrencyInput
                  onValueChange={ this.onAssocDuesChange }
                  value={ form.fields['associationDues'].value }
                  fieldName="associationDues"
                  placeholder="Association Dues"
                  min={ 0 }
                  isInvalid={ cxValidateField(form.fields['associationDues']) }>
                </CurrencyInput>
              </div>
            </div> : null
          }

        </div>

        { this.getEssentialsAreaBlock(props, state) }

        { this.getEssentialsFurnishingBlock(props, state) }

        { this.getEssentialsBedroomBlock(props, state) }

        { this.getEssentialsBathroomBlock(props, state) }

        <div className="row mt-4">
          <div className="col-12 mt-4">
            <div className="form-group mb-0">
              <label className="hi-fast-track-form__form-group-label">Availability Date*</label>
              <DatePickerControl onChange={ evt => this.ondateAvailabilitySelected(evt.target['value']) }
                value={ form.fields['dateAvailability'].value }
                className={ cx('form-control', {
                  'is-invalid': cxValidateField(form.fields['dateAvailability'])
                }) } />
            </div>
            { this.getErrorBlock(state, 'dateAvailability', 'Please select an availability date') }
          </div>
        </div>

        <div className="row mt-4">
          { this.getEssentialsAmenitiesBlock(props, state) }
        </div>

        { this.getEssentialsVideoLinkBlock(props, state) }

        { this.getEssentialsAlternateContactBlock(state) }

        { this.getEssentialsPrivateNotesBlock(state) }

      </div>
    );
  }

  getMinLeaseTermBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;

    if (state.currentDivCat.category === 'Rent') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Minimum Lease Term (in years)*</label>
              <div className="d-flex mb-1">
              {
                LeaseTerms.map(term => 
                  <span className={ cx('hi-input-radio-toggle px-1',
                    {
                      active: form.fields['minLeaseTerm'].value == term.value
                    }) 
                    }>
                    <label>
                      <input className="first" 
                        type="radio" 
                        name="minLeaseTerm" 
                        value={term.value}
                        onChange={ hopplerLinkState(this.form$, form, 'minLeaseTerm') }/>
                      { term.label == '5' ? '5+' : term.label }
                    </label>
                  </span>  
                )
              }
              </div>
              { this.getErrorBlock(state, 'minLeaseTerm', 'Please select a lease term') }
            </div>
          </div>
        </div>
      );
    }
  }

  onAssocDuesChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'associationDues', value);
  }

  onIsPresellingChange = () => {
    this.setState ({
      isPresale: !this.state.isPresale
    })
  }

  onDiscountPriceChange = (value) => {    
    let form = this.state.postPropertyForm;
    let price = form.fields['salePrice'].value;
    if ( this.state.currentDivCat.division === 'Commercial') {
      price = form.fields['pricePerSqm'].value;      
    } else {
      if (this.state.currentDivCat.category === 'Rent') {
        price = form.fields['rentalPrice'].value;
      };
    };
    if (value === "") {
      this.setState({
        discountedPercentage: 0
      });
    } else {
      let discount = 100 - ((value / price) * 100);
      this.setState({
        discountedPercentage: +discount.toFixed(2)
      });
    };
    hopplerUpdateFormFieldValue(this.form$, form, 'discountedPrice', value);
  }

  onRentalPriceChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'rentalPrice', value);
  }

  onSalePriceChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'salePrice', value);
  }

  onPricePerSqmChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'pricePerSqm', value);
  }

  onFloorAreaChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'pricePerSqm', form.fields['pricePerSqm'].value);
    hopplerUpdateFormFieldValue(this.form$, form, 'floorArea', value);
  }

  onLotAreaChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'pricePerSqm', form.fields['pricePerSqm'].value);
    hopplerUpdateFormFieldValue(this.form$, form, 'lotArea', value);
  }

  onParkingSpacesChange = (value) => {
    let form = this.state.postPropertyForm;

    hopplerUpdateFormFieldValue(this.form$, form, 'parkingSpaces', value);
  }

  getPricePerSqmBlock(state: PostPropertyState) {
    let form = state.postPropertyForm;

    return (
      <div className="col-12">
        <div className="hi-input-group">
          <label className="hi-input-group__label">Price per Sqm*</label>
          <CurrencyInput
            onValueChange={ this.onPricePerSqmChange }
            value={ form.fields['pricePerSqm'].value }
            fieldName="pricePerSqm"
            placeholder="Price per Sqm"
            min={ 0 }
            isInvalid={ cxValidateField(form.fields['pricePerSqm']) }>
          </CurrencyInput>
        </div>
        {
          state.isPpsqmNotEnough ? 
          <div className="hi-error-help-text">
            <i className="fas fa-exclamation-circle"></i> Calculated rent must be at least ₱20,000
          </div> 
          : this.getErrorBlock(state, 'pricePerSqm', 'Please add a price per sqm')
        }
      </div>
    );
  }

  getEssentialsDiscountBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    return (
      <div className="row mt-4">
        <div className="col-6">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Discounted Price</label>
            <DiscountInput
              onValueChange={ this.onDiscountPriceChange }
              value={ form.fields['discountedPrice'].value }
              fieldName="discountedPrice"
              placeholder="Discounted Price"
              isInvalid={ cxValidateField(form.fields['discountedPrice']) }>
            </DiscountInput>
          </div>
          { this.getErrorBlock(state, 'discountedPrice', 'Please add a valid discount price') }
        </div>
        <div className="col-6">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Discount Percentage (%)</label>
              <input
                disabled
                value={ state.discountedPercentage }
                className="form-control hi-text-input"
                placeholder="%"
                type="text"
              />
          </div>
        </div>
      </div>
    );
  }

  getEssentialsPriceBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    let isCommercial = state.currentDivCat.division === 'Commercial';

    if (state.currentDivCat.category === 'Rent') {
      return (
        <div className="row">
          {
            !isCommercial ? 
            <div className="col-12">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Monthly Rental Price</label>
                <CurrencyInput
                  onValueChange={ this.onRentalPriceChange }
                  value={ form.fields['rentalPrice'].value }
                  fieldName="rentalPrice"
                  placeholder="Rental Price"
                  min={ 0 }
                  isInvalid={ cxValidateField(form.fields['rentalPrice']) }>
                </CurrencyInput>
              </div>
              { this.getErrorBlock(state, 'rentalPrice', 'Please add a rental price') }
            </div> : null
          }
          {
            isCommercial ? this.getPricePerSqmBlock(state) : null
          }
        </div>
      );
    }

    if (state.currentDivCat.category === 'Sale') {
      return (
        <div className="row">
          {
            !isCommercial ? 
            <div className="col-12">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Sale Price</label>
                <CurrencyInput
                  onValueChange={ this.onSalePriceChange }
                  value={ form.fields['salePrice'].value }
                  fieldName="salePrice"
                  placeholder="Sale Price"
                  min={ 0 }
                  isInvalid={ cxValidateField(form.fields['salePrice']) }>
                </CurrencyInput>
              </div>
              { this.getErrorBlock(state, 'salePrice', 'Please add a sale price') }
            </div> : null
          }
          {
            isCommercial ? this.getPricePerSqmBlock(state) : null
          }
        </div>
      );
    }
    
  }

  getEssentialsAreaBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    let type = form.fields['type'].value;
    let hasFloorArea = type !== 'Vacant Lot' && type !== 'Commercial Lot';
    
    
    return (
      <div className="row mt-4">
        {
          hasFloorArea ?
          <div className="col">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Floor Area*</label>
              {/* <input 
                className={ cx('form-control hi-text-input',
                  {
                    'invalid': Validators.cxValidateField(form.fields['floorArea'])
                  })
                } 
                placeholder="Floor area" 
                type="number"
                onInput={ hopplerLinkState(this.form$, form, 'floorArea') }/> */}
                <NumberInput
                  onValueChange={ this.onFloorAreaChange }
                  value={ form.fields['floorArea'].value }
                  fieldName="floorArea"
                  placeholder="Floor Area"
                  min={ 0 }
                  isInvalid={ cxValidateField(form.fields['floorArea']) }>
                </NumberInput>
            </div>
            { this.getErrorBlock(state, 'floorArea', 'Please add a floor area') }
          </div> : null
        }

        {
          !state.requiresBuilding ? 
            <div className="col">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Lot Area*</label>
                {/* <input 
                  className={ cx('form-control hi-text-input',
                    {
                      'invalid': Validators.cxValidateField(form.fields['lotArea'])
                    })
                  } 
                  placeholder="Lot area" 
                  type="number"
                  onInput={ hopplerLinkState(this.form$, form, 'lotArea') }/> */}
                  <NumberInput
                    onValueChange={ this.onLotAreaChange }
                    value={ form.fields['lotArea'].value }
                    fieldName="lotArea"
                    placeholder="Lot Area"
                    min={ 0 }
                    isInvalid={ cxValidateField(form.fields['lotArea']) }>
                  </NumberInput>
              </div>
              { this.getErrorBlock(state, 'lotArea', 'Please add a lot area') }
            </div> : null
        }

      </div>
    );
  }

  getEssentialsFurnishingBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    const { division, type } = form.fields;

    if (division.value === 'Residential' && type.value !== 'Vacant Lot') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Furnishing*</label>
              <div className="row mb-1">
                {
                  FurnishingChoices.map((option) => 
                  <span className="hi-input-group__checkbox col-12 col-lg-3 mr-0">
                    <label>
                      <input
                        type="radio" name="furnishing" 
                        onChange={ hopplerLinkState(this.form$, form, 'furnishing') }
                        value={option.value} 
                        checked={form.fields['furnishing'].value == option.value}
                        />
                      { option.label }
                    </label>
                  </span>
                  )
                }
              </div>
              { this.getErrorBlock(state, 'furnishing', 'Please select a furnishing') }
            </div>
          </div>
        </div>
      );
    } else if (division.value === 'Commercial') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Condition*</label>
              <div className="row mb-1">
                {
                  ConditionChoices.map((option) => 
                  <span className="hi-input-group__checkbox col-3 mr-0">
                    <label>
                      <input
                        type="radio" name="condition" 
                        onChange={ hopplerLinkState(this.form$, form, 'condition') }
                        value={option.value} 
                        checked={form.fields['condition'].value == option.value}
                        />
                      { option.label }
                    </label>
                  </span>
                  )
                }
              </div>
              { this.getErrorBlock(state, 'condition', 'Please select a condition') }
            </div>
          </div>
        </div>
      );
    }

  }

  getEssentialsBedroomBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    const { division, type } = form.fields;
    
    if (division.value === 'Residential' && type.value !== 'Vacant Lot') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Bedrooms*</label>
              <div className="d-flex mb-1">
                {
                  form.fields['type'].value !== 'House and Lot' &&
                  form.fields['type'].value !== 'Vacant Lot' ?
                  <span className="hi-input-group__checkbox mr-3">
                    <label>
                      <input
                        type="radio" name="bedrooms"
                        onChange={ hopplerLinkState(this.form$, form, 'bedrooms') }
                        checked={form.fields['bedrooms'].value == 0}
                        value={0}/>
                        Studio
                    </label>
                  </span> : null
                }
                {
                  [1,2,3,4,5].map((option) => 
                  <span className="hi-input-group__checkbox mr-3">
                    <label>
                      <input
                        type="radio" name="bedrooms" 
                        onChange={ hopplerLinkState(this.form$, form, 'bedrooms') }
                        value={option as number}
                        checked={form.fields['bedrooms'].value == option}
                        />
                      { option > 4 ? '5+' : option }
                    </label>
                  </span>
                  )
                }
              </div>
              { this.getErrorBlock(state, 'bedrooms', 'Please select the number of bedrooms') }
            </div>
          </div>
        </div>
      );
    }
  }

  getEssentialsBathroomBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    const { division, type } = form.fields;

    if (division.value === 'Residential' && type.value !== 'Vacant Lot') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Bathrooms*</label>
              <div className="d-flex mb-1">
                {
                  [1,2,3,4,5].map((option) => 
                  <span className="hi-input-group__checkbox mr-3">
                    <label>
                      <input
                        type="radio" name="bathrooms" 
                        onChange={ hopplerLinkState(this.form$, form, 'bathrooms') }
                        value={option} 
                        checked={form.fields['bathrooms'].value == option}
                        />
                      { option > 4 ? '5+' : option }
                    </label>
                  </span>
                  )
                }
              </div>
              { this.getErrorBlock(state, 'bathrooms', 'Please select the number of bathrooms') }
            </div>
          </div>
        </div>
      );
    } else if (division.value === 'Commercial') {
      return (
        <div className="row mt-4">
          <div className="col-12">
            <div className="hi-input-group">
              <label className="hi-input-group__label">Restrooms</label>
              <div className="d-flex mb-1">
                {
                  [1,2,3,4,5].map((option) => 
                  <span className="hi-input-group__checkbox mr-3">
                    <label>
                      <input
                        type="radio" name="bathrooms" 
                        onChange={ hopplerLinkState(this.form$, form, 'bathrooms') }
                        value={option} 
                        checked={form.fields['bathrooms'].value == option}
                        />
                      { option > 4 ? '5+' : option }
                    </label>
                  </span>
                  )
                }
              </div>
              { this.getErrorBlock(state, 'bathrooms', 'Please select the number of restrooms') }
            </div>
          </div>
          <div className="col-12 mt-4">
            <select className="form-control" onChange={ hopplerLinkState(this.form$, form, 'restRoom') }
                value={ form.fields['restRoom'].value }>
              <option className="text-muted" value="" disabled={true} selected>Choose Restroom Type</option>
              <option value="Common">Common</option>
              <option value="Private">Private</option>
              <option value="Provisioned">Provisioned</option>
            </select>
          </div>
        </div>
      );
    }

  }

  getEssentialsAmenitiesBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;
    let type: string = form.fields['type'].value.toLocaleLowerCase();

    const amenitiesToUse = this.dcUtils.defineAmenities(type);

    return (
      <div className="col-12 hi-listing-editor-modal__editor__amenities">
        <label className="hi-input-group__label">Amenities</label>
        <div className="row">
          {
            amenitiesToUse.map(member => 
              <div className="col-12 col-md-4">
                <label>
                  <input type="checkbox" name={member.field}
                      checked={form.fields[member.field].value == 1}
                      value={1}
                      onChange={ hopplerLinkState(this.form$, form, member.field) }
                    />
                  <span className="ml-2">
                    { member.label }
                  </span>
                </label>
              </div>
            )
          }
        </div>
        {
          form.fields['__hasParking'].value ?
          <div className="row mt-3">
            <div className="col">
              <div className="hi-input-group">
                <label className="hi-input-group__label">Number of Parking Spaces*</label>
                {/* <input 
                  className={ cx('form-control hi-text-input',
                    {
                      'invalid': Validators.cxValidateField(form.fields['parkingSpaces'])
                    })
                  } 
                  placeholder="Parking Spaces" 
                  type="number"
                  onInput={ hopplerLinkState(this.form$, form, 'parkingSpaces') }/> */}
                  <NumberInput
                    onValueChange={ this.onParkingSpacesChange }
                    value={ form.fields['parkingSpaces'].value }
                    fieldName="parkingSpaces"
                    placeholder="Parking Spaces"
                    min={ 0 }
                    isInvalid={ cxValidateField(form.fields['parkingSpaces']) }>
                  </NumberInput>
              </div>
              { this.getErrorBlock(state, 'parkingSpaces', 'Please add the number of parking spaces') }
            </div>
          </div> : null
        }
      </div>
    );
  }

  getEssentialsVideoLinkBlock(props: PostPropertyProps, state: PostPropertyState) {
    const form = state.postPropertyForm;

    return (
      <div className="row mt-4">
        <div className="col-12">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Video Link</label>
            <input className="form-control hi-text-input" type="text"
              placeholder="https://www.youtube.com/..."
              onInput={ hopplerLinkState(this.form$, form, 'videoUrl') }/>
          </div>
        </div>
      </div>
    );
  }

  getEssentialsAlternateContactBlock(state: PostPropertyState) {
    let form = state.postPropertyForm;

    return (
      <div className="row mt-4 hi-listing-editor-modal__editor__alt-contact">
        <div className="col-12">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Alternative Contact Person</label>
            <input className="form-control hi-text-input" placeholder="Name" type="text"
              onInput={ hopplerLinkState(this.form$, form, 'contactName') }/>
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Alternative Contact Person's Contact Number</label>
            <input className="form-control hi-text-input" placeholder="Contact Number" type="text"
              onInput={ hopplerLinkState(this.form$, form, 'contactNumber') }/>
          </div>
        </div>

        <div className="col-12 mt-4">
          <div className="hi-input-group">
            <label className="hi-input-group__label">Alternative Contact Person's Email Address</label>
            <input className="form-control hi-text-input" placeholder="Email Address" type="text"
              onInput={ hopplerLinkState(this.form$, form, 'contactEmail') }/>
          </div>
        </div>
      </div>
    );
  }

  getEssentialsPrivateNotesBlock(state: PostPropertyState) {
    let form = state.postPropertyForm;

    return (
      <div className="row mt-4">
        <div className="col-12">
          <label className="hi-input-group__label">Private Notes</label>
          <textarea placeholder="Write your message here"
            className="form-control hi-listing-editor-modal__editor__notes"
            value={ form.fields['notes'].value }
            onInput={ hopplerLinkState(this.form$, form, 'notes') } >
          </textarea>
        </div>
      </div>
    );
  }

  // -------- REVIEW SECTION --------

  getReviewBlock(props: PostPropertyProps, state: PostPropertyState) {
    let form = state.postPropertyForm;

    return (
      <div className="col-12">
        <PropertyPage 
          listing={state.currentListing}
          onEdit={() => this.onTabHeaderClicked('essentials')}
          onSave={this.postProperty}
          submitPropertyStatus={state.submitPropertyStatus} />
      </div>
    );
  }

  getErrorBlock(state: PostPropertyState, fieldName: string, message: string) {
    if (Validators.cxValidateField(state.postPropertyForm.fields[fieldName])) {
      return (
        <div className="hi-error-help-text">
          <i className="fas fa-exclamation-circle"></i> { message }
        </div>
      );
    }
  }

  getMultiFieldErrorBlock(state: PostPropertyState, fieldNames: string[], message: string) {

    let errorCount: number = 0;

    fieldNames.forEach(fieldName => {
      if (Validators.cxValidateField(state.postPropertyForm.fields[fieldName])) {
        errorCount++;
      }
    })

    if (errorCount > 0) {
      return (
        <div className="hi-error-help-text">
          <i className="fas fa-exclamation-circle"></i> { message }
        </div>
      );
    }
  }

  getNextButton(state: PostPropertyState) {
    if (state.currentStage === 'type') {
      return (
        <button className="btn btn-secondary" disabled={ !state.currentDivCat }
          onClick={ e => this.onNext(state.currentStage)}>
          Next
        </button>
      )
    } else if (state.currentStage === 'location') {
      return (
        <button className="btn btn-secondary" 
          disabled={ 
            !state.currentDivCat || 
            !state.currentLocation || 
            !state.locationForm.isValid ||
            state.hasDupe ||
            state.submitDraftStatus == REQUEST_IN_PROGRESS
          }
          onClick={ e => this.onNext(state.currentStage)}>
          Next
        </button>
      )
    } else if (state.currentStage === 'photos') {
      return (
        <button className="btn btn-secondary" 
          disabled={ !state.currentDivCat || !state.currentLocation || !state.locationForm.isValid }
          onClick={ e => this.onNext(state.currentStage)}>
          Next
        </button>
      )
    } else {
      if (state.submitPropertyStatus  == REQUEST_IN_PROGRESS) {
        return this.getSpinnerBlock();
      } else {
        return (
          <button className="btn btn-secondary" 
            disabled={ !state.postPropertyForm.isValid }
            onClick={ this.generateListingForDescription }>
            Review
          </button>
        );
      }
    }
  }

  getBackButton(state: PostPropertyState) {
    if (state.currentStage !== 'type') {
      return (
        <button className="btn btn-outline-dark back" onClick={ e => this.onBack(state.currentStage) }>
          Back
        </button>
      );
    }
  }

  onNext = (stage: string) => {
    switch(stage) {
      case 'type':
        this.setState({
          currentStage: 'location'
        });

        break;

      case 'location':
        this.setState({
          currentStage: 'photos'
        });

        break;

      case 'photos':
        this.setState({
          currentStage: 'essentials'
        });
        break;

      case 'essentials':
        this.setState({
          currentStage: 'review'
        });
        break;

      default:
        this.setState({
          currentStage: 'type'
        });
        break;
    }
  }

  onBack = (stage: string) => {
    switch(stage) {
      case 'location':
        this.setState({
          currentStage: 'type'
        });
        break;

      case 'photos':
        this.setState({
          currentStage: 'location'
        });

        break;

      case 'essentials':
        this.setState({
          currentStage: 'photos'
        });
        break;

      default:
        this.setState({
          currentStage: 'type'
        });
        break;
    }
  }

  onTabHeaderClicked = (stage: string) => {

    this.setState({
      currentStage: stage
    })
  }

  getSpinnerBlock() {
    return (
      <div className="mx-3">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  getActionsBlock(state: PostPropertyState) {
    if (!state.isReviewingPhotos || state.currentStage !== 'photos') {
      return (
        <div className="hi-postprop__actions">
          <div className="d-flex hi-postprop__actions__button-container first">
            { this.getBackButton(state) }

            <div className="ml-1 w-50">
              { state.submitDraftStatus == REQUEST_IN_PROGRESS ? this.getSpinnerBlock() : null }

              {/* Button must be disabled if location has a duplicate and any async process is ongoing */}
              
              <button className="btn btn-outline-dark" 
                onClick={this.saveAsDraft}
                disabled={
                  state.hasDupe ||
                  state.submitDraftStatus == REQUEST_IN_PROGRESS ||
                  state.submitPropertyStatus == REQUEST_IN_PROGRESS
                }
                >
                Save as draft
              </button>
            </div>
            
          </div>

          <div className="d-flex mt-3 mt-md-0 ml-md-3 hi-postprop__actions__button-container">
            { this.getNextButton(state) }
          </div>
        </div>
      );
    } else {
      return (
        <div className="hi-postprop__actions">

          <div className="d-flex mt-3 mt-md-0 ml-md-3 hi-postprop__actions__button-container">
            { state.photoUploadState == REQUEST_IN_PROGRESS ? this.getSpinnerBlock() : null }
            
            <button className="btn btn-outline-dark" 
                disabled={state.photoUploadState === REQUEST_IN_PROGRESS}
                onClick={this.onCancelPhotoUpload}>
              Cancel
            </button>

            <button className="btn btn-secondary" 
                disabled={state.photoUploadState === REQUEST_IN_PROGRESS}
                onClick={this.onUploadPhotos}>
              Upload
            </button>
          </div>
          
        </div>
      );
    }
  }

  getPageContent(props: PostPropertyProps, state: PostPropertyState) {

    if (state.currentStage === 'review') {
      return this.getReviewBlock(props, state);
    } else if (state.currentStage !== 'submit') {
      return (
        <div className="col-12 col-lg-8 offset-lg-2">
          <div className="hi-postprop__form-container">
            <div className="hi-postprop__tab-headers">
              <button onClick={e => this.onTabHeaderClicked('type')}
                  className={ cx('hi-postprop__tab-headers__tab', 
                  { 'active': state.currentStage === 'type' }) }>
                Type
              </button>
              <button onClick={e => this.onTabHeaderClicked('location')}
                  className={ cx('hi-postprop__tab-headers__tab', 
                  { 'active': state.currentStage === 'location' }) }
                  disabled={ !state.currentDivCat || !state.currentDivCat.type }>
                Location
              </button>
              <button onClick={e => this.onTabHeaderClicked('photos')}
                  className={ cx('hi-postprop__tab-headers__tab', 
                  { 'active': state.currentStage === 'photos' }) }
                  disabled={ !state.currentDivCat || !state.currentLocation || !state.locationForm.isValid || state.hasDupe }>
                Photos
              </button>
              <button onClick={e => this.onTabHeaderClicked('essentials')}
                  className={ cx('hi-postprop__tab-headers__tab', 
                  { 'active': state.currentStage === 'essentials' }) }
                  disabled={ !state.currentDivCat || !state.currentLocation || !state.locationForm.isValid || state.hasDupe }>
                Essentials
              </button>
            </div>

            <div className="hi-postprop__tab-content">
              { this.getTabContentBlock(props, state) }
            </div>

            { this.getActionsBlock(state) }

          </div>
        </div>
      );
    } else {
      return (
        <div className="col-12 col-lg-8 offset-lg-2">
          <div className="hi-postprop__success">
            <h3 className="mt-5">
              Excellent! You've successfully submitted your listing. 
            </h3>

            <p className="mt-4">
              You will be notified when your listing goes online.
            </p>

            <button className="btn btn-secondary mt-5" onClick={this.resetComponent}>
              Upload new property
            </button>

          </div>
        </div>
      );
    }
  }

  render(props: PostPropertyProps, state: PostPropertyState) {
    return (
      <div className="hi-postprop">
        <div className="container-fluid">

          <div className="row">
            <div className="col-8 col-lg-4">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><a href="#">Post Property</a></li>
                  <li className="breadcrumb-item active" aria-current="page">{ state.currentStage }</li>
                </ol>
              </nav>
            </div>

            <div className="col-4 col-lg-2 offset-lg-6 d-flex justify-content-end align-items-center">
              {/* <a className="hi-kumita__dashboard__faq" href="#">
                FAQs <i className="fas fa-chevron-right"></i>
              </a> */}
            </div>
          </div>

          <div className="row mt-2 mb-4 no-gutters hi-postprop__row">

            { this.getPageContent(props, state) }

          </div>

        </div>
      </div>
    );
  }
}